import { useState, useEffect } from 'react';
import { Loader2, Save, ChevronRight, ChevronLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { upsertVentasPeriodo } from './services/ventasPeriodo.services';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatCurrency = (v) =>
  v != null && !isNaN(v)
    ? `$${Number(v).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : '$0';

const fetchTodosLosClientes = async () => {
  const res = await fetch(`${API_URL}/client/filter`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ limit: 1000, offset: 1, query: { estado: 'Activo' } }),
  });
  if (!res.ok) throw new Error('Error al cargar clientes activos');
  const json = await res.json();
  return json.data || [];
};

const ParticipacionVentasModal = ({ isOpen, onClose, onSaved, periodoExistente }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [periodo, setPeriodo] = useState(getCurrentMonth());
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [ventas, setVentas] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setPeriodo(getCurrentMonth());
    setClientes([]);
    setVentas({});
  }, [isOpen]);

  // Pre-fill with existing data when editing
  useEffect(() => {
    if (!isOpen || !periodoExistente) return;
    setPeriodo(periodoExistente.periodo || getCurrentMonth());
    if (periodoExistente.clientes?.length > 0) {
      const init = {};
      periodoExistente.clientes.forEach(c => { init[c.id] = c.ventas > 0 ? String(c.ventas) : ''; });
      // Defer to step 2 after loading clients
    }
  }, [isOpen, periodoExistente]);

  const handleCargarClientes = async () => {
    setLoadingClientes(true);
    try {
      const data = await fetchTodosLosClientes();
      setClientes(data);

      // Pre-fill ventas if editing an existing period
      const init = {};
      data.forEach(c => {
        const existing = periodoExistente?.clientes?.find(ec => ec.id === c._id);
        init[c._id] = existing?.ventas > 0 ? String(existing.ventas) : '';
      });
      setVentas(init);
      setStep(2);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoadingClientes(false);
    }
  };

  // Group clients by center
  const grupos = clientes.reduce((acc, c) => {
    const centroId = c.lavanderia?.id || 'sin-centro';
    const centroNombre = c.lavanderia?.nombre || 'Sin centro asignado';
    if (!acc[centroId]) acc[centroId] = { centroId, centroNombre, clientes: [] };
    acc[centroId].clientes.push(c);
    return acc;
  }, {});

  const totalGeneral = clientes.reduce((s, c) => s + (parseFloat(ventas[c._id]) || 0), 0);

  const totalCentro = (centroId) =>
    (grupos[centroId]?.clientes || []).reduce((s, c) => s + (parseFloat(ventas[c._id]) || 0), 0);

  const pctGeneral = (clienteId) => {
    const v = parseFloat(ventas[clienteId]) || 0;
    return totalGeneral > 0 ? (v / totalGeneral * 100) : 0;
  };

  const pctCentro = (clienteId, centroId) => {
    const v = parseFloat(ventas[clienteId]) || 0;
    const tc = totalCentro(centroId);
    return tc > 0 ? (v / tc * 100) : 0;
  };

  const handleSave = async () => {
    const [añoNum, mesNum] = periodo.split('-').map(Number);

    const clientesData = clientes.map(c => {
      const centroId = c.lavanderia?.id || '';
      const centroNombre = c.lavanderia?.nombre || '';
      const v = parseFloat(ventas[c._id]) || 0;
      const tc = totalCentro(centroId);
      return {
        id: c._id,
        nombre: c.nombre,
        centroId,
        centroNombre,
        ventas: v,
        participacionGeneral: totalGeneral > 0 ? parseFloat((v / totalGeneral * 100).toFixed(4)) : 0,
        participacionPorCentro: tc > 0 ? parseFloat((v / tc * 100).toFixed(4)) : 0,
      };
    });

    setIsSaving(true);
    try {
      await upsertVentasPeriodo({ periodo, año: añoNum, mes: mesNum, totalVentas: totalGeneral, clientes: clientesData });
      toast({ title: 'Ventas guardadas', description: `Período ${mesLabel} ${añoStr} registrado correctamente` });
      onSaved(periodo);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const [añoStr, mesStr] = periodo ? periodo.split('-') : ['', ''];
  const mesLabel = MESES[parseInt(mesStr) - 1] || '';

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            Participación de Ventas
            {step === 2 && periodo && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                · {mesLabel} {añoStr}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Steps */}
        <div className="flex items-center gap-2 shrink-0">
          <div className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full transition-colors ${step === 1 ? 'bg-primary text-primary-foreground' : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'}`}>
            <span>1</span>
            <span>Seleccionar período</span>
          </div>
          <ChevronRight className="h-3 w-3 text-muted-foreground" />
          <div className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full transition-colors ${step === 2 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
            <span>2</span>
            <span>Registrar ventas</span>
          </div>
        </div>

        {/* ── PASO 1 ── */}
        {step === 1 && (
          <div className="space-y-5 py-2">
            <div className="space-y-2">
              <Label>Año y mes de referencia</Label>
              <Input
                type="month"
                className="w-[200px]"
                value={periodo}
                onChange={e => setPeriodo(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Se cargarán todos los clientes activos actualmente para registrar sus ventas de este período.
              </p>
            </div>

            {periodoExistente && (
              <div className="text-xs bg-amber-500/10 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 text-amber-700 dark:text-amber-300">
                Este período ya tiene ventas registradas. Al continuar podrás editarlas.
              </div>
            )}

            <Button onClick={handleCargarClientes} disabled={loadingClientes || !periodo}>
              {loadingClientes
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando clientes...</>
                : 'Cargar clientes'}
            </Button>
          </div>
        )}

        {/* ── PASO 2 ── */}
        {step === 2 && (
          <div className="flex flex-col gap-3 min-h-0 flex-1">
            {/* Summary bar */}
            <div className="shrink-0 flex flex-wrap items-center gap-4 text-xs text-muted-foreground bg-muted/30 rounded-lg px-3 py-2">
              <span><strong className="text-foreground">{clientes.length}</strong> clientes activos</span>
              <span>·</span>
              <span>Total ventas: <strong className="text-foreground">{formatCurrency(totalGeneral)}</strong></span>
              <span>·</span>
              <span><strong className="text-foreground">{Object.keys(grupos).length}</strong> centros</span>
            </div>

            {/* Table */}
            <div className="border border-border rounded-lg overflow-hidden flex-1 min-h-0">
              <div className="overflow-y-auto max-h-[45vh]">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Cliente</th>
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground w-40">Ventas (COP)</th>
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground w-24">% General</th>
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground w-24">% Centro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(grupos).map(grupo => {
                      const tc = totalCentro(grupo.centroId);
                      return (
                        <>
                          {/* Centro header */}
                          <tr key={`hdr-${grupo.centroId}`} className="bg-blue-500/8 border-t-2 border-border">
                            <td colSpan={4} className="px-3 py-1.5">
                              <span className="font-semibold text-blue-700 dark:text-blue-300">{grupo.centroNombre}</span>
                              <span className="ml-3 text-muted-foreground font-normal">
                                {grupo.clientes.length} clientes · {formatCurrency(tc)}
                                {totalGeneral > 0 && <span className="ml-1 text-blue-600 dark:text-blue-400">({(tc / totalGeneral * 100).toFixed(1)}% del total)</span>}
                              </span>
                            </td>
                          </tr>
                          {grupo.clientes.map(c => (
                            <tr key={c._id} className="border-t border-border/60 hover:bg-muted/20">
                              <td className="px-3 py-1 pl-7 text-muted-foreground">{c.nombre}</td>
                              <td className="px-2 py-0.5">
                                <Input
                                  type="number"
                                  min="0"
                                  placeholder="0"
                                  className="h-7 text-xs text-right"
                                  value={ventas[c._id] ?? ''}
                                  onChange={e => setVentas(prev => ({ ...prev, [c._id]: e.target.value }))}
                                />
                              </td>
                              <td className="px-3 py-1 text-right tabular-nums font-medium">
                                {pctGeneral(c._id).toFixed(2)}%
                              </td>
                              <td className="px-3 py-1 text-right tabular-nums text-blue-600 dark:text-blue-400">
                                {pctCentro(c._id, grupo.centroId).toFixed(2)}%
                              </td>
                            </tr>
                          ))}
                        </>
                      );
                    })}
                  </tbody>
                  <tfoot className="border-t-2 border-border">
                    <tr className="bg-muted/20 font-semibold">
                      <td className="px-3 py-2">Total — {clientes.length} clientes</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(totalGeneral)}</td>
                      <td className="px-3 py-2 text-right">100%</td>
                      <td className="px-3 py-2 text-right text-muted-foreground text-xs font-normal">por centro</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {totalGeneral === 0 && (
              <p className="shrink-0 text-xs text-amber-600 dark:text-amber-400 bg-amber-500/5 rounded px-3 py-2 border border-amber-200 dark:border-amber-800">
                Sin ventas ingresadas. Al distribuir gastos con este período, se aplicará reparto equitativo.
              </p>
            )}
          </div>
        )}

        <DialogFooter className="shrink-0 pt-2">
          {step === 2 && (
            <Button variant="outline" size="sm" onClick={() => setStep(1)} disabled={isSaving} className="mr-auto">
              <ChevronLeft className="h-4 w-4 mr-1" /> Atrás
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={isSaving}>Cancelar</Button>
          {step === 2 && (
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</>
                : <><Save className="mr-2 h-4 w-4" /> Guardar ventas</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ParticipacionVentasModal;
